// @owners { team: patients }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { get } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { queryClient } from '~shared/react-query/queryClient';
import { type LightDelivery } from '~shared/types';

const DELIVERIES_API_VERSION = 'v3';

const fetchDeliveriesRequest = async (params: Record<string, unknown>): Promise<LightDelivery[]> => {
  const response = await get('/deliveries', params, { version: DELIVERIES_API_VERSION });
  if (response.error) {
    throw new Error(getErrorMessageFromResponse(response));
  }
  return response;
};

/**
 * Two independent delivery queries (Option D):
 * - `all` is the expensive full list (every historical delivery) — refreshed rarely (long staleTime).
 * - `active` is the cheap in-flight subset — refreshed often (short staleTime).
 * Consumers read a read-time merge of the two (see useGetDeliveries), mirroring the old
 * "fetch all occasionally + refresh active frequently + upsert" behavior without the write-time merge.
 */
export const deliveries = createQueryKeys('deliveries', {
  all: {
    queryKey: null,
    queryFn: () => fetchDeliveriesRequest({ batchley: true }),
  },
  active: {
    queryKey: null,
    queryFn: () => fetchDeliveriesRequest({ batchley: true, active: true }),
  },
});

/** Invalidate both delivery queries (all + active) so active observers refetch. */
export const invalidateDeliveries = () => queryClient.invalidateQueries({ queryKey: deliveries._def });

/**
 * Optimistically remove canceled deliveries from both query caches so the UI updates immediately
 * without a full refetch — mirrors the old CANCEL_DELIVERIES_SUCCEEDED reducer behavior.
 */
export const removeDeliveriesFromCache = (canceledIds: number[]) => {
  const idSet = new Set(canceledIds);
  // Prefix-match on the scope key so every deliveries query (all + active, and any future subkey) is covered.
  queryClient.setQueriesData<LightDelivery[]>({ queryKey: deliveries._def }, (current) =>
    current ? current.filter((delivery) => !idSet.has(delivery.id)) : current,
  );
};
